import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Printer,
  Receipt,
  Loader2,
  TicketCheck,
  ScanLine,
  Search,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { EmptyState } from '@/components/pos/EmptyState';
import { ReceiptPreview } from '@/components/pos/ReceiptPreview';
import { useSaleDetail, usePrintBatch, useReprintSingle } from '@/hooks/mutations';
import { useDeviceStore } from '@/stores/device.store';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useHotkeys } from '@/hooks/useHotkeys';
import { printReceipt } from '@/lib/printReceipt';
import type { SaleDetail } from '@/types/sale';

/**
 * /print-ticket — dedicated "print an existing booking" flow.
 *
 * The fast path for a customer who bought online (or lost their printout):
 * type or SCAN the booking number, and reprint every ticket. Distinct from
 * /orders (which is a browse/search surface) — this is a single-shot lookup
 * tuned for a keyboard-wedge scanner and a big print button.
 */
export function PrintTicketPage() {
  const [params, setParams] = useSearchParams();
  const printerBound = useDeviceStore((s) => Boolean(s.ticketPrinterId));

  const [code, setCode] = useState(params.get('booking')?.toUpperCase() ?? '');
  const [detail, setDetail] = useState<SaleDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: fetchDetail, isPending: looking } = useSaleDetail();
  const { mutate: printBatch, isPending: printing } = usePrintBatch();
  const { mutate: reprintOne } = useReprintSingle();

  const lookup = (raw?: string) => {
    const q = (raw ?? code).trim().toUpperCase();
    if (!q) {
      toast.warning('Masukkan nomor pesanan dulu.');
      inputRef.current?.focus();
      return;
    }
    setCode(q);
    setParams(q ? { booking: q } : {}, { replace: true });
    setNotFound(false);
    fetchDetail(q, {
      onSuccess: (d) => {
        if (!d || !d.invoiceId) {
          setDetail(null);
          setNotFound(true);
          return;
        }
        setDetail(d);
      },
      onError: (err) => {
        setDetail(null);
        setNotFound(true);
        toast.error(err.message || 'Pesanan tidak ditemukan.');
      },
    });
  };

  // Auto-lookup when arriving with ?booking= (e.g. from Beranda / a scan on
  // another page that routed here).
  useEffect(() => {
    const initial = params.get('booking');
    if (initial) lookup(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard-wedge scanner: a scan anywhere on the page runs the lookup.
  // `flushOnIdleMs` auto-searches as soon as the scan burst finishes — no need
  // for the scanner to send a trailing Enter.
  useBarcodeScanner({ onScan: (scanned) => lookup(scanned), flushOnIdleMs: 120 });

  const handlePrintAll = () => {
    if (!detail) return;
    if (!printerBound) {
      toast.warning('Printer belum diatur — hubungkan printer di menu Perangkat.');
      return;
    }
    printBatch(detail.invoiceId, {
      onSuccess: (res) => {
        const ok = res.tickets.filter((t) => t.status === 'PRINTED').length;
        if (ok === res.tickets.length) toast.success(`${ok} tiket dicetak.`);
        else toast.warning(`${ok}/${res.tickets.length} tiket dicetak. Cek printer.`);
      },
      onError: (err) => toast.error(err.message),
    });
  };

  useHotkeys({ 'ctrl+p': handlePrintAll });

  const printDisabled =
    !detail || !printerBound || printing || ['CANCELLED', 'REFUND'].includes(String(detail.status));

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-6 overflow-y-auto px-6 py-8 pos-scroll">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-ink-900">
          <TicketCheck className="h-6 w-6 text-crimson-700" /> Cetak Tiket
        </h1>
        <p className="text-sm text-muted-foreground">
          Masukkan atau pindai nomor pesanan untuk mencetak tiketnya.
        </p>
      </div>

      {/* Lookup — scanner-friendly, oversized for touch + quick reads. */}
      <Card>
        <CardContent className="pt-6">
          <label className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">
            <ScanLine className="h-3.5 w-3.5" /> Nomor pesanan
          </label>
          <div className="flex gap-3">
            <input
              ref={inputRef}
              data-scanner-target
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') lookup();
              }}
              placeholder="INV-AP-…"
              className="tnum h-16 flex-1 rounded-xl border-2 border-cream-200 bg-white px-4 font-display text-2xl font-bold uppercase tracking-wide text-ink-900 outline-none transition-colors placeholder:font-sans placeholder:text-base placeholder:font-normal placeholder:tracking-normal placeholder:text-ink-400 focus:border-crimson-600"
            />
            <Button
              size="lg"
              className="h-16 gap-2 px-8 text-lg"
              onClick={() => lookup()}
              disabled={looking}
            >
              {looking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
              Cari
            </Button>
          </div>
          {!printerBound && (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-amber-600">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Printer belum diatur — atur di menu Perangkat sebelum mencetak.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Result */}
      <Card className="flex min-h-0 flex-1 flex-col">
        <CardContent className="flex min-h-0 flex-1 flex-col pt-6">
          {looking ? (
            <div className="flex flex-1 items-center justify-center py-16 text-ink-400">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : notFound ? (
            <EmptyState
              icon={Search}
              title="Pesanan tidak ditemukan"
              description={`Tidak ada pesanan dengan nomor "${code}". Periksa kembali nomornya.`}
              className="flex-1 border-0 bg-transparent"
            />
          ) : !detail ? (
            <EmptyState
              icon={TicketCheck}
              title="Siap mencetak"
              description="Masukkan atau pindai nomor pesanan di atas untuk menampilkan tiketnya."
              className="flex-1 border-0 bg-transparent"
            />
          ) : (
            <>
              <ScrollArea className="flex-1">
                <ReceiptPreview detail={detail} />
              </ScrollArea>
              {/* Print-only clone — the browser prints just this subtree
                  (see the .printing-receipt thermal rules in index.css). */}
              <div id="receipt-print" aria-hidden="true" className="hidden">
                <ReceiptPreview detail={detail} />
              </div>
              <Separator className="my-4" />
              <div className="flex gap-2">
                <Button
                  size="lg"
                  className="h-14 flex-1 gap-2 text-lg"
                  onClick={handlePrintAll}
                  disabled={printDisabled}
                >
                  {printing ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Printer className="h-5 w-5" />
                  )}
                  Cetak Semua Tiket
                  <kbd className="ml-1 rounded border border-primary-foreground/30 px-1.5 text-xs">
                    Ctrl+P
                  </kbd>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 gap-2 text-base"
                  onClick={printReceipt}
                >
                  <Receipt className="h-5 w-5" />
                  Cetak Struk
                </Button>
              </div>
              {['CANCELLED', 'REFUND'].includes(String(detail.status)) && (
                <p className="mt-2 text-center text-sm text-amber-600">
                  Pesanan {String(detail.status).toLowerCase()} — tiket tidak dapat dicetak.
                </p>
              )}
              {(detail.tickets?.length ?? 0) > 0 && (
                <div className="mt-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
                    Cetak per tiket
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {detail.tickets?.map((t) => (
                      <Button
                        key={t.id}
                        variant="outline"
                        size="sm"
                        disabled={!printerBound || printing}
                        onClick={() =>
                          reprintOne(t.id, {
                            onSuccess: () => toast.success(`${t.productName ?? 'Tiket'} dicetak.`),
                            onError: (err) => toast.error(err.message),
                          })
                        }
                      >
                        {t.productName ?? t.id}
                        {t.visitorType ? ` · ${t.visitorType}` : ''}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
